/**
 * In-app booking offer send via Gmail (Apps Script as sunsetcomedylondon@gmail.com).
 * @see docs/APP_UI_SPEC.md — B4
 * @see prototype/booking-gmail-send/README.md
 */
import Swal from 'sweetalert2';
import { SUNSET_BOOKING_MAILBOX } from './bookingEmail.js';

/**
 * Dev must never hit a real comic from the live club mailbox.
 * Keep in sync with supabase/functions/_shared/booking-gmail-delivery.ts
 *
 * @param {{ to: string, body: string, env?: string, devRecipient?: string }} opts
 * @returns {{ to: string, body: string, redirected: boolean, intendedTo?: string, error?: string }}
 */
export function resolveBookingGmailDelivery({ to, body, env, devRecipient }) {
  const isProd = String(env || '').toLowerCase() === 'prod';
  if (isProd) {
    return { to, body, redirected: false };
  }
  const dest = String(devRecipient || '').trim();
  if (!dest) {
    return { to, body, redirected: false, error: 'dev_recipient_required' };
  }
  return {
    to: dest,
    body:
      `[DEV] Intended recipient: ${to}\n` +
      `This message was redirected so the club mailbox can be tested without emailing the comic.\n\n` +
      body,
    redirected: true,
    intendedTo: to,
  };
}

/**
 * @param {{ data?: object|null, error?: { message?: string, context?: { status?: number } }|null }} result
 * @returns {{ kind: 'sent', deliveredTo: string, redirected: boolean } | { kind: 'skipped', reason: string } | { kind: 'error', reason: string }}
 */
export function interpretBookingGmailInvoke({ data, error }) {
  if (data?.ok === true) {
    return {
      kind: 'sent',
      deliveredTo: data.delivered_to || '',
      redirected: Boolean(data.redirected),
    };
  }
  if (data?.skipped === true) {
    return { kind: 'skipped', reason: data.reason || 'not_configured' };
  }
  if (error) {
    const status = error.context?.status;
    const msg = String(error.message || error);
    if (status === 404 || /not found/i.test(msg)) {
      return { kind: 'skipped', reason: 'function_not_deployed' };
    }
    return { kind: 'error', reason: msg };
  }
  return { kind: 'error', reason: data?.reason || 'send_failed' };
}

export function shouldWarnDevGmailRedirect(hostname) {
  const host = String(hostname || '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host.startsWith('dev.');
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Edit subject/body before send. Cancel returns null.
 *
 * @param {{ to: string, subject: string, body: string }} draft
 * @returns {Promise<{ to: string, subject: string, body: string }|null>}
 */
export async function promptEditBookingOffer(draft) {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const devNote = shouldWarnDevGmailRedirect(hostname)
    ? ` On the <strong>dev</strong> project this is redirected to the test recipient (you), not the comic.`
    : '';
  const { isConfirmed, value } = await Swal.fire({
    title: 'Review booking email',
    width: '40rem',
    html: `<p class="booking-offer-compose-meta">From <strong>${escapeHtml(SUNSET_BOOKING_MAILBOX)}</strong>.${devNote}</p>
<label class="booking-offer-compose-label" for="swal-booking-offer-to">To</label>
<input id="swal-booking-offer-to" class="swal2-input booking-offer-compose-input" style="width:100%;margin:0 0 10px;" value="${escapeHtml(draft.to)}" />
<label class="booking-offer-compose-label" for="swal-booking-offer-subject">Subject</label>
<input id="swal-booking-offer-subject" class="swal2-input booking-offer-compose-input" style="width:100%;margin:0 0 10px;" value="${escapeHtml(draft.subject)}" />
<label class="booking-offer-compose-label" for="swal-booking-offer-body">Body</label>
<textarea id="swal-booking-offer-body" class="swal2-textarea booking-offer-compose-body" style="width:100%;min-height:14em;margin:0;">${escapeHtml(draft.body)}</textarea>
<p class="booking-offer-compose-hint">Keep the subject starting with “You Are Booked” so replies still reach the inbox pipeline.</p>`,
    showCancelButton: true,
    confirmButtonText: 'Send',
    cancelButtonText: 'Cancel',
    focusConfirm: false,
    customClass: { popup: 'booking-offer-compose-popup' },
    preConfirm: () => {
      const to = document.getElementById('swal-booking-offer-to')?.value?.trim() || '';
      const subject = document.getElementById('swal-booking-offer-subject')?.value?.trim() || '';
      const body = document.getElementById('swal-booking-offer-body')?.value || '';
      if (!to || !subject || !body.trim()) {
        Swal.showValidationMessage('To, subject, and body are required');
        return false;
      }
      if (!/^You Are Booked/i.test(subject)) {
        Swal.showValidationMessage('Subject must start with “You Are Booked” so replies are tracked');
        return false;
      }
      return { to, subject, body };
    },
  });

  if (!isConfirmed || !value) return null;
  return value;
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {{ to: string, subject: string, body: string, cc?: string }} payload
 */
export async function invokeSendBookingOffer(supabase, payload) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return { kind: 'error', reason: 'no_session' };
  }

  const { data, error } = await supabase.functions.invoke('send-booking-offer', {
    body: {
      to: payload.to,
      subject: payload.subject,
      body: payload.body,
      cc: payload.cc || '',
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  return interpretBookingGmailInvoke({ data, error });
}
